import {
  Experiment,
  ExperimentConfig,
  type Exposure,
  type Variant,
  type Variants,
} from '@amplitude/experiment-js-client';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { Atom, atom, useAtomValue } from 'jotai';
import { v4 as uuid } from 'uuid';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { datadog } from '@flipster/utils/datadog';
import { EnvScope } from '@flipster/utils/env';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { segmentBunja } from '@flipster/utils/segment/bunja';

import { userIdBunja } from '../auth/state';

export function useVariant(flagKey: string): string | undefined {
  const { getVariantAtom } = useBunja(abTestBunja);
  const { variantAtom } = getVariantAtom(flagKey);
  return useAtomValue(variantAtom);
}

interface AbTestEvents {
  exposure: Exposure;
}

type RawVariantAtom = Atom<Variant | undefined>;
interface VariantAtomFamily {
  rawVariantAtom: RawVariantAtom;
  variantAtom: Atom<string>;
}

export const abTestBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const env = bunja.use(EnvScope);
  const { userIdForTrackAtom } = bunja.use(userIdBunja);
  const { anonymousIdAtom } = bunja.use(segmentBunja);

  const events = new EventEmitter<AbTestEvents>();
  const initialVariants = getInitialVariants();
  const hasInitialVariants = Boolean(initialVariants) && Object.keys(initialVariants).length > 0;
  const experimentConfig: ExperimentConfig = {
    serverUrl: 'https://api2.lab.amplitude.com',
    exposureTrackingProvider: {
      track: (exposure) => {
        events.emit('exposure', exposure);
        segment.track('$exposure', exposure);
      },
    },
  };
  // 타입상 initialVariants 필드에 undefined를 넣을 수 있다고 나오나
  // 실제로 null이나 undefined를 대입해보면 런타임 오류가 발생함
  if (initialVariants) experimentConfig.initialVariants = initialVariants;
  const experiment = Experiment.initialize(env.AMPLITUDE_DEPLOYMENT_KEY, experimentConfig);

  function getUserId() {
    return store.get(userIdForTrackAtom);
  }

  function getDeviceId() {
    return store.get(anonymousIdAtom);
  }

  function getInitialVariants() {
    const userIdOrDeviceId = getUserId() || getDeviceId();
    if (!userIdOrDeviceId) return {};
    return getLocalStorage(`flipster.configs.v1.amplitude.${userIdOrDeviceId}.variants`) as Variants;
  }

  const variantAtoms: Record<string, VariantAtomFamily> = {};
  function getVariantAtom(flagKey: string): VariantAtomFamily {
    if (variantAtoms[flagKey]) return variantAtoms[flagKey];
    const rawVariantAtom = atom((get) => {
      const userIdOrDeviceId = getUserId() || getDeviceId();
      const isFetching = get(fetchingAtom);
      if (isFetching && !hasInitialVariants) return;
      if (!userIdOrDeviceId) return;
      return experiment.variant(flagKey);
    });
    const variantAtom = atom((get) => {
      const variant = get(rawVariantAtom)?.value || 'off';
      if (flagKey) datadog.setContext(`ab-test-flag.${flagKey}`, variant);
      return variant;
    });
    return (variantAtoms[flagKey] = { rawVariantAtom, variantAtom });
  }

  function getVariant(flagKey: string): string {
    return store.get(getVariantAtom(flagKey).variantAtom);
  }

  // prexInstallationIdAtom은 uuid 값으로 초기화되며 localStorage에 저장된다. 로컬스토리지에 있는지 확인하고 없으면 초기화한다.
  const prexInstallationIdAtom = atom(() => {
    const prexInstallationId = getLocalStorage('flipster.configs.v1.prexInstallationId');
    if (!prexInstallationId) {
      const newPrexInstallationId = uuid();
      setLocalStorage('flipster.configs.v1.prexInstallationId', newPrexInstallationId);
      return newPrexInstallationId;
    }
    return prexInstallationId;
  });

  function getPrexInstallationId() {
    return store.get(prexInstallationIdAtom);
  }

  const fetchingAtom = atom(false);
  const fetchedAtom = atom(false);

  async function fetchExperiment() {
    const userIdOrDeviceId = getUserId() || getDeviceId();
    if (!userIdOrDeviceId) return experiment.clear();
    try {
      store.set(fetchingAtom, true);
      const prexInstallationId = store.get(prexInstallationIdAtom);
      /**
       * amplitude에서 실험 생성 시 롤아웃 버킷팅 대상을 무조건 prex_installation_id 로 설정해야 한다.
       * https://prextech.slack.com/archives/C08LLK73DDE/p1754546594730269?thread_ts=1753328755.697299&cid=C08LLK73DDE
       */
      await experiment.fetch({
        user_id: getUserId(),
        device_id: getDeviceId(),
        user_properties: {
          prex_installation_id: prexInstallationId,
        },
      });
      store.set(fetchedAtom, true);
      setLocalStorage(`flipster.configs.v1.amplitude.${userIdOrDeviceId}.variants`, experiment.all());
    } finally {
      store.set(fetchingAtom, false);
    }
  }

  bunja.effect(() => {
    const unsubUserId = store.sub(userIdForTrackAtom, fetchExperiment);
    const unsubDeviceId = store.sub(anonymousIdAtom, fetchExperiment);
    // 웹소켓 부분 구독 이후 다시 추가하도록 함
    // const unsubWsEvent = r230522Events.on('adhoc/public', (adhoc) => {
    //   if (adhoc === 'ReloadABTestUrgently') fetchExperiment();
    // });
    const oneMinute = 1000 * 60;
    const interval = setInterval(fetchExperiment, oneMinute * 10);
    fetchExperiment();
    return () => {
      unsubUserId();
      unsubDeviceId();
      // unsubWsEvent();
      clearInterval(interval);
      experiment.clear();
    };
  });

  return {
    events,
    experiment,
    getVariantAtom,
    getVariant,
    getPrexInstallationId,
    fetchingAtom,
    fetchedAtom,
  };
});
